import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ComprehensiveDashboard from './ComprehensiveDashboard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Admin "View as user" – loads the same data the client sees and renders
 * the same ComprehensiveDashboard so the admin sees exactly the same UI/UX.
 */
function AdminViewAsUser({ onBack }) {
  const { clientId } = useParams();
  const [userStatus, setUserStatus] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_URL}/api/admin/view-as/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) {
          const msg = await res.json().catch(() => ({}));
          throw new Error(msg.message || `Failed to load client (${res.status})`);
        }
        const { userStatus: us, dashboardData: dd } = await res.json();
        if (cancelled) return;
        setUserStatus(us);
        setDashboardData(dd || null);
        const enhancedClient = {
          'Id no': us.clientId,
          'Name': us.name,
          initialInvestment: dd?.summary?.totalInvestment ?? 0,
          withdrawn: dd?.summary?.totalWithdrawn ?? 0,
          currentValue: dd?.summary?.currentValue ?? 0,
          totalReturn: dd?.summary?.totalReturn ?? 0,
          profitLoss: dd?.summary?.profitLoss ?? 0,
          userType: us.userType,
          lastUpdatedDate: dd?.performance?.lastUpdated ?? new Date().toISOString(),
          ...us.data,
        };
        setClient(enhancedClient);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load view-as data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-medium" style={{ fontFamily: 'Lexend, sans-serif' }}>
            Loading client view...
          </p>
        </div>
      </div>
    );
  }

  if (error || !userStatus || !client) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="bg-[#151515] border border-white/10 rounded-xl p-8 max-w-md w-full text-center">
          <p className="text-red-400 mb-4">{error || 'Client not found'}</p>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#676DFF] hover:bg-[#5A60E8] text-white rounded-lg transition-colors"
            style={{ fontFamily: 'Lexend, sans-serif' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]">
      {/* Sticky banner: viewing as [Name] | back to admin */}
      <div
        className="sticky top-0 z-50 flex items-center justify-between gap-4 px-4 py-2 bg-[#151515] border-b border-white/10"
        style={{ fontFamily: 'Lexend, sans-serif' }}
      >
        <span className="text-sm text-[#B8B8B8]">
          Viewing as <strong className="text-white">{client.Name}</strong> (ID: {clientId})
        </span>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Admin
        </button>
      </div>
      <div className="flex-1">
        <ComprehensiveDashboard
          client={client}
          userStatus={userStatus}
          dashboardData={dashboardData}
          onLogout={onBack}
          onStatusUpdate={() => {}}
          isAdminPreview
        />
      </div>
    </div>
  );
}

export default AdminViewAsUser;
